import { toInteger } from 'lodash';
import { calculateGradientCoordinate, createCanvas } from '@/utils/file-kit';
import type { ILayerProps, ILinearGradient, IQrCodeLayerProps } from '@/version-types';
import { convertToQRCodeInfo } from '../utils/converter';

/**
 * 将 QR 码内容直接绘制到 Canvas（不经过 DOM 渲染）
 *
 * @param qrCodeSize - QR 码原始尺寸
 * @param path - QR 码 SVG path 数据
 * @param fill - 背景色
 * @param stroke - 前景色
 * @param strokeGradient - 前景渐变色
 * @param canvasWidth - 目标画布宽度
 * @param canvasHeight - 目标画布高度
 */
const drawQRCodeToCanvas = (
    qrCodeSize: number,
    path: string,
    fill: string,
    stroke: string,
    strokeGradient: ILinearGradient | null,
    canvasWidth: number,
    canvasHeight: number,
) => {
    const { canvas, ctx } = createCanvas(canvasWidth, canvasHeight);

    // 缩放坐标系以匹配 SVG viewBox
    const scaleX = canvasWidth / qrCodeSize;
    const scaleY = canvasHeight / qrCodeSize;
    ctx.scale(scaleX, scaleY);

    // 绘制背景
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, qrCodeSize, qrCodeSize);

    // 绘制 QR 码路径
    // qrcode 库生成的 path 数据是水平线段（M4 4.5h1m1 0h1...），需要用 stroke 绘制
    const path2d = new Path2D(path);
    ctx.lineWidth = 1;
    ctx.lineCap = 'butt';

    if (strokeGradient) {
        const { x0, y0, x1, y1 } = calculateGradientCoordinate(qrCodeSize, qrCodeSize, strokeGradient.angle);
        const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
        for (const colorStop of strokeGradient.colors) {
            gradient.addColorStop(colorStop.value, colorStop.color);
        }
        ctx.strokeStyle = gradient;
    } else {
        ctx.strokeStyle = stroke;
    }

    ctx.stroke(path2d);

    return canvas;
};

/**
 * 对 QR 码画布应用图层变换（裁剪、翻转、旋转、缩放）并可选地应用透明度
 *
 * @param layer - 图层属性
 * @param baseImage - QR 码画布
 * @param opacity - 透明度，默认 1（不透明）
 */
const applyLayerTransform = (layer: ILayerProps, baseImage: HTMLCanvasElement, opacity = 1, retinaScale = 1) => {
    const { width, height } = layer;
    const {
        imageTransform: {
            flipH,
            flipV,
            scale,
            rotate: imgRotate,
            widthRatio,
            heightRatio,
            offsetX: offsetXRatio,
            offsetY: offsetYRatio,
        },
    } = layer.data as IQrCodeLayerProps;

    const resultWidth = toInteger(width * retinaScale);
    const resultHeight = toInteger(height * retinaScale);
    const offsetX = toInteger(offsetXRatio * widthRatio * width * scale * retinaScale);
    const offsetY = toInteger(offsetYRatio * heightRatio * height * scale * retinaScale);
    const originImgWidth = toInteger(width * widthRatio * retinaScale);
    const originImgHeight = toInteger(height * heightRatio * retinaScale);

    let imgOffsetX = resultWidth / 2;
    let imgOffsetY = resultHeight / 2;

    if (flipH) {
        imgOffsetX = imgOffsetX - offsetX;
    } else {
        imgOffsetX = imgOffsetX + offsetX;
    }

    if (flipV) {
        imgOffsetY = imgOffsetY - offsetY;
    } else {
        imgOffsetY = imgOffsetY + offsetY;
    }

    let rotate = imgRotate;

    // ATTENTION: 以下针对 rotate 的处理，是因为翻转是要针对旋转后图片，而不是旋转后的坐标系进行翻转，需要脑补一下画面
    if (flipH) {
        rotate = -rotate;
    }

    if (flipV) {
        rotate = -rotate;
    }

    const { canvas: destCanvas, ctx: destCtx } = createCanvas(resultWidth, resultHeight);

    destCtx.globalAlpha = opacity;
    destCtx.translate(imgOffsetX, imgOffsetY);
    destCtx.rotate((rotate * Math.PI) / 180);
    destCtx.scale(scale * (flipH ? -1 : 1), scale * (flipV ? -1 : 1));
    destCtx.drawImage(baseImage, -originImgWidth / 2, -originImgHeight / 2, originImgWidth, originImgHeight);

    return destCanvas;
};

export const renderLayerContentToCanvas = async (layer: ILayerProps, globalAlpha = true, scale = 1) => {
    const { width, height, opacity } = layer;
    const { text, fill, stroke, strokeGradient } = layer.data as IQrCodeLayerProps;

    if (width <= 0 || height <= 0) return createCanvas(1, 1).canvas;

    const qrCodeInfo = await convertToQRCodeInfo(text);

    if (!qrCodeInfo.path) {
        return createCanvas(1, 1).canvas;
    }

    const qrCanvas = drawQRCodeToCanvas(
        qrCodeInfo.size,
        qrCodeInfo.path,
        fill,
        stroke,
        strokeGradient,
        toInteger(width * scale),
        toInteger(height * scale),
    );

    const layerCanvas = applyLayerTransform(layer, qrCanvas, globalAlpha ? opacity : 1, scale);
    return layerCanvas;
};
